import type { AxiosResponse } from "axios";
import { type CountryCode, isValidPhoneNumber } from "libphonenumber-js";
import { useCallback, useEffect, useMemo } from "react";
import { z } from "zod";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { SubdivisionField } from "@/shared/components/address/SubdivisionField";
import { ColocaDateField } from "@/shared/components/forms/ColocaDateField";
import { ColocaForm } from "@/shared/components/forms/ColocaForm";
import { ColocaInputField } from "@/shared/components/forms/ColocaInputField";
import { ColocaPhoneField } from "@/shared/components/forms/ColocaPhoneField";
import { ColocaSelectField } from "@/shared/components/forms/ColocaSelectField";
import { useZodForm } from "@/shared/components/forms/useZodForm";
import { useCountryLabel } from "@/shared/hooks/useCountryLabel";
import useTranslations from "@/shared/lang/useTranslations";
import { HttpApiService } from "@/shared/services/httpApi.service";
import { colocaToast } from "@/shared/utils/toast";

import {
  documentLengthMessageKey,
  documentTypeLabelKey,
  documentTypesFor,
  isDocumentNumberLengthValid,
} from "../../constants/identityDocumentTypes.constants";
import { useCountryScopedDocumentType } from "../../hooks/useCountryScopedDocumentType";
import { useIsCryptomateOrg } from "../../hooks/useIsCryptomateOrg";
import { IdentitiesService } from "../../services/identitiesService";
import { AddressType as AddressTypeEnum } from "../../types/identities-api.types";
import type {
  CreateIdentityResponseDto,
  CreateIndividualIdentityRequest,
} from "../../types/identity-form.types";
import { countryItems, firstOfferedIdentityCountry, normalizePhone, phoneSeedFor } from "./holder-form.utils";
import { ThreeDsInfoButton } from "./ThreeDsInfoButton";

const buildPersonSchema = (
  phoneError: string,
  birthdateError: string,
  idTypeError: string,
  documentLengthError: (code: string) => string | undefined,
) =>
  z
    .object({
      email: z.string().email(),
      first_name: z.string().min(1).max(255),
      last_name: z.string().min(1).max(255),
      birthdate: z
        .string()
        .min(1)
        .regex(/^\d{4}-\d{2}-\d{2}$/, birthdateError),
      gender: z.enum(["M", "F", "OTHER"]),
      nationality: z.string().length(3),
      id_country: z.string().length(3),
      identification_type: z.string().min(1),
      identification_number: z.string().min(1).max(100),
      phone: z.string().min(1),
      phone_country: z.string().length(2),
      address_line_1: z.string().min(1).max(50),
      address_line_2: z.string().max(50).optional().or(z.literal("")),
      city: z.string().min(1).max(50),
      state: z.string().min(1).max(50),
      country: z.string().length(3),
      postal_code: z.string().min(1).max(50),
    })
    .superRefine((data, ctx) => {
      if (!isValidPhoneNumber(data.phone, data.phone_country as CountryCode)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["phone"],
          message: phoneError,
        });
      }

      // The API validates the document type against the document country, so mirror that check here
      // instead of letting the request fail with a 400.
      const allowedIdTypes: readonly string[] = documentTypesFor(data.id_country, "personal");
      if (!allowedIdTypes.includes(data.identification_type)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["identification_type"],
          message: idTypeError,
        });
      }

      // Same rule the clients module applies: the API checks the character set but never the length, so a
      // mistyped CPF is only caught here. Both screens create the same identity, so both must agree.
      if (!isDocumentNumberLengthValid(data.identification_type, data.identification_number)) {
        const message = documentLengthError(data.identification_type);
        if (message) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["identification_number"],
            message,
          });
        }
      }
    });

type PersonValues = z.infer<ReturnType<typeof buildPersonSchema>>;

/** The form's own values, for a host that pre-fills it (`initialValues`). */
export type PersonHolderFormValues = PersonValues;

/** Used when the session carries no usable country — a country we do not onboard from, or no session. */
const FALLBACK_COUNTRY = "COL";

const PERSON_DEFAULTS: PersonValues = {
  email: "",
  first_name: "",
  last_name: "",
  birthdate: "",
  gender: "M",
  nationality: FALLBACK_COUNTRY,
  id_country: FALLBACK_COUNTRY,
  // Derived so the preselected type is always valid for the preselected document country.
  identification_type: documentTypesFor(FALLBACK_COUNTRY, "personal")[0],
  identification_number: "",
  phone: "",
  phone_country: "",
  address_line_1: "",
  address_line_2: "",
  city: "",
  state: "",
  country: FALLBACK_COUNTRY,
  postal_code: "",
};

type PersonHolderFormProps = {
  formId: string;
  onSuccess?: (response?: CreateIdentityResponseDto) => void;
  /**
   * Override the create call. Defaults to `IdentitiesService.createIdentity` (POST /internal/identities). The
   * verification flow passes a function that posts to `verification/person`.
   */
  submit?: (payload: CreateIndividualIdentityRequest) => Promise<AxiosResponse<CreateIdentityResponseDto>>;
  /** Show the built-in success toast. Defaults to true. */
  showSuccessToast?: boolean;
  onSubmittingChange?: (submitting: boolean) => void;
  /**
   * Values to open the form with, over the session-derived defaults. Read once, on mount: remount the form
   * (`key`) to load another set.
   */
  initialValues?: Partial<PersonHolderFormValues>;
  /**
   * Hand the validated payload to the host instead of creating the identity: no request, no toast. For a host
   * that sends it somewhere else, behind its own confirmation.
   */
  onValidSubmit?: (payload: CreateIndividualIdentityRequest) => void | Promise<void>;
};

export function PersonHolderForm({
  formId,
  onSuccess,
  submit,
  showSuccessToast = true,
  onSubmittingChange,
  initialValues,
  onValidSubmit,
}: PersonHolderFormProps) {
  const { t, activeLanguage } = useTranslations("cards");
  const countryLabel = useCountryLabel();
  const countryOptions = useMemo(
    () => countryItems(countryLabel, activeLanguage),
    [countryLabel, activeLanguage],
  );
  const showThreeDs = useIsCryptomateOrg();
  const documentLengthError = useCallback(
    (code: string) => {
      const key = documentLengthMessageKey(code);
      return key ? t(key) : undefined;
    },
    [t],
  );
  const personSchema = useMemo(
    () =>
      buildPersonSchema(
        t("create_holder_phone_invalid_for_country"),
        t("create_holder_birthdate_invalid"),
        t("create_identity_error_id_type_invalid_for_country"),
        documentLengthError,
      ),
    [t, documentLengthError],
  );
  // Seeded from the session rather than from a constant: this form only runs authenticated, so the country
  // the person chose when they registered is known. The person's own country wins over the organization's —
  // this is their identity, not the company's (`BusinessHolderForm` reads them the other way round for the
  // same reason). Each is checked against the onboarding allowlist in turn, so a country we do not offer
  // falls THROUGH to the next one instead of ending the search, and the selectors never open on a value they
  // do not have.
  const { user } = useAuth();
  const initialCountry =
    firstOfferedIdentityCountry(user?.country, user?.organization?.country) ?? FALLBACK_COUNTRY;
  const initialPhone = useMemo(() => phoneSeedFor(initialCountry), [initialCountry]);
  const defaults = useMemo<PersonValues>(
    () => ({
      ...PERSON_DEFAULTS,
      nationality: initialCountry,
      id_country: initialCountry,
      // Kept derived from the document country, as before: a preselected type invalid for it is a 400.
      identification_type: documentTypesFor(initialCountry, "personal")[0],
      country: initialCountry,
      phone_country: initialPhone.iso2,
      phone: initialPhone.dialCode,
      ...initialValues,
    }),
    [initialCountry, initialPhone, initialValues],
  );
  const form = useZodForm(personSchema, defaults);

  // Document types are country-dependent on the backend (`@IsValidDocumentType`), so the
  // `identification_type` options follow the selected document country. A person holder shows the "personal"
  // category (Personal ID) — company tax IDs (fiscal) never appear here.
  const idTypeCodes = useCountryScopedDocumentType(
    form,
    "id_country",
    "identification_type",
    "personal",
    "identification_number",
  );
  const idTypeItems = useMemo(
    () => idTypeCodes.map((code) => ({ value: code, label: t(documentTypeLabelKey(code)) })),
    [idTypeCodes, t],
  );

  const isSubmitting = form.formState.isSubmitting;
  useEffect(() => {
    onSubmittingChange?.(isSubmitting);
  }, [isSubmitting, onSubmittingChange]);

  const handleSubmit = async (values: PersonValues) => {
    const payload: CreateIndividualIdentityRequest = {
      type: "individual",
      address: {
        address_line_1: values.address_line_1,
        // `@IsString()` without `@IsOptional()` on the API: an empty line must travel as "", never null.
        address_line_2: values.address_line_2?.trim() ?? "",
        city: values.city,
        state: values.state,
        country: values.country,
        type: AddressTypeEnum.REGISTRATION,
        postal_code: values.postal_code,
      },
      identity: {
        email: values.email.trim(),
        phone: normalizePhone(values.phone),
        first_name: values.first_name.trim(),
        last_name: values.last_name.trim(),
        birthdate: values.birthdate,
        gender: values.gender,
        country: values.id_country,
        nationality: values.nationality,
        identification_type: values.identification_type,
        identification_number: values.identification_number.trim(),
      },
    };

    if (onValidSubmit) {
      await onValidSubmit(payload);
      return;
    }

    try {
      const submitFn: (
        p: CreateIndividualIdentityRequest,
      ) => Promise<AxiosResponse<CreateIdentityResponseDto>> = submit ?? IdentitiesService.createIdentity;
      const { data } = await submitFn(payload);
      if (showSuccessToast) colocaToast.success(t("create_holder_success"));
      onSuccess?.(data);
    } catch (err: unknown) {
      colocaToast.error(HttpApiService.getErrorMessage(err) || t("identities_error_load"));
    }
  };

  return (
    <ColocaForm form={form} onSubmit={handleSubmit} id={formId} className="space-y-6">
      <section className="space-y-3">
        <h3 className="text-sm font-semibold text-foreground">{t("create_holder_section_personal")}</h3>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <ColocaInputField name="first_name" label={t("create_identity_label_first_name")} />
          <ColocaInputField name="last_name" label={t("create_identity_label_last_name")} />
          <ColocaInputField name="email" label={t("create_identity_label_email")} type="email" />
          <ColocaPhoneField
            name="phone"
            label={
              showThreeDs ? (
                <span className="inline-flex items-center gap-1.5">
                  {t("create_identity_label_phone")}
                  <ThreeDsInfoButton />
                </span>
              ) : (
                t("create_identity_label_phone")
              )
            }
            countryFieldName="phone_country"
            defaultCountry={initialPhone.iso2 || undefined}
          />
          <ColocaDateField
            name="birthdate"
            label={t("create_identity_label_birthdate")}
            maxDate={new Date()}
            fromYear={1900}
            toYear={new Date().getFullYear()}
          />
          <ColocaSelectField
            name="gender"
            label={t("create_identity_label_gender")}
            items={[
              { value: "M", label: t("create_identity_gender_male") },
              { value: "F", label: t("create_identity_gender_female") },
              { value: "OTHER", label: t("create_identity_gender_other") },
            ]}
          />
          <ColocaSelectField
            name="nationality"
            label={t("create_identity_label_nationality")}
            items={countryOptions}
          />
          <ColocaSelectField
            name="id_country"
            label={t("create_identity_label_id_country")}
            items={countryOptions}
          />
          <ColocaSelectField
            name="identification_type"
            label={t("create_identity_label_identification_type")}
            items={idTypeItems}
          />
          <ColocaInputField
            name="identification_number"
            label={t("create_identity_label_identification_number")}
          />
        </div>
      </section>

      <section className="space-y-3">
        <h3 className="text-sm font-semibold text-foreground">{t("create_holder_section_address")}</h3>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <ColocaSelectField
            name="country"
            label={t("create_identity_label_country")}
            items={countryOptions}
          />
          <ColocaInputField name="city" label={t("create_identity_label_city")} />
          <SubdivisionField name="state" countryName="country" label={t("create_identity_label_state")} />
          <ColocaInputField name="postal_code" label={t("create_identity_label_postal_code")} />
          <ColocaInputField name="address_line_1" label={t("create_identity_label_address_line_1")} />
          <ColocaInputField name="address_line_2" label={t("create_identity_label_address_line_2")} />
        </div>
      </section>
    </ColocaForm>
  );
}
